import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import InviteAccept from "./pages/InviteAccept";
import PasswordReset from "./pages/PasswordReset";
import SignLease from "./pages/SignLease";
import CreateLease from "./pages/CreateLease";
import LeaseList from "./pages/LeaseList";
import Properties from "./pages/Properties";
import AddProperty from "./pages/AddProperty";
import LeaseView from "./pages/LeaseView";
import Maintenance from "./pages/Maintenance";
import Service from "./pages/Service";
import Messages from "./pages/Messages";
import MessagesDemo from "./pages/MessagesDemo";
import Unsubscribe from "./pages/Unsubscribe";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SmsConsentDemo from "./pages/SmsConsentDemo";
import Settings from "./pages/Settings";
import SettingsDemo from "./pages/SettingsDemo";
import Tenants from "./pages/Tenants";
import Contractors from "./pages/Contractors";
import TenantDashboard from "./pages/TenantDashboard";
import TenantSettings from "./pages/TenantSettings";
import TenantSignup from "./pages/TenantSignup";
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from "./pages/LandingPage";
//import PrivateRoute from "./pages/PrivateRoute"; // Protect routes


function AuthExpiredRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = () => {
      const params = new URLSearchParams(window.location.search);
      // Preserve a ?reason= param from the current URL (e.g. set when an
      // expired short-code link forwards an unauthenticated visitor here) so
      // the login page can explain why they landed there.
      const reason = params.get('reason');
      // Preserve an invitation token so an expired-mid-accept session doesn't
      // abandon the invite: the invite-accept page carries it as ?token=, and
      // login continues the flow via /login?invite=<token>.
      const invite = params.get('invite') || params.get('token');
      const loginParams = new URLSearchParams();
      if (invite) loginParams.set('invite', invite);
      if (reason) loginParams.set('reason', reason);
      const qs = loginParams.toString();
      navigate(qs ? `/login?${qs}` : '/login');
    };
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, [navigate]);
  return null;
}

function App() {
  // Add a safety net to catch any remaining SignWell errors
  // (The main fix is the contentWindow protection in LeaseView.js)
  useEffect(() => {
    const handleError = (event) => {
      // Check if this is a SignWell-related error
      const isPostMessageError = event.message && (
        event.message.includes('postMessage') ||
        event.message.includes('contentWindow')
      );
      
      const isSignWellFile = event.filename && event.filename.includes('signwell');
      
      if (isPostMessageError || isSignWellFile) {
        // Silently suppress SignWell-related errors
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      
      return false;
    };

    window.addEventListener('error', handleError, true);
    
    return () => {
      window.removeEventListener('error', handleError, true);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <AuthExpiredRedirect />
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/addproperty" element={<AddProperty />} />
          <Route path="/login" element={<Login />} />
          <Route path="/invite/accept" element={<InviteAccept />} />
          <Route path="/reset" element={<PasswordReset />} />
          <Route path="/signlease" element={<SignLease />} />
          <Route path="/leases" element={<LeaseList />} />
          <Route path="/lease/:leaseId" element={<LeaseView />} />
          <Route path="/createlease" element={<CreateLease />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/service" element={<Service />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages-demo" element={<MessagesDemo />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/sms-consent-demo" element={<SmsConsentDemo />} />
          <Route path="/tenants" element={<Tenants />} />
          <Route path="/contractors" element={<Contractors />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings-demo" element={<SettingsDemo />} />
          <Route path="/tenant/signup" element={<TenantSignup />} />
          <Route path="/tenant/dashboard" element={<TenantDashboard />} />
          <Route path="/tenant/settings" element={<TenantSettings />} />
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
